import React from "react";

import type { CheckoutLineItem } from "pages/app/workspace/sections/helpers/account-section.helpers";
import {
  CheckoutOtpCheckboxRow,
  CheckoutOtpCheckboxText,
  FieldErrorText,
  FieldLabel,
  FormField,
  FormInput,
  FormSelect,
  IconActionButton,
  MutedPanel,
  PanelBodyStack,
  PanelHeaderRow,
  PanelTitleText,
  PrimaryActionButton,
  SecondaryActionButton,
  SectionFormGrid,
  SectionStack,
} from "pages/app/workspace/sections/styles/workspace-section.styles";

type QuickOrderFormProps = {
  isSubmitting: boolean;
  merchandiseError?: string;
  merchandise: any[];
  onMerchandiseItemChange: (value: string) => void;
  onQuantityChange: (value: number) => void;
  onSubmit: () => void;
  quantityError?: string;
  quantity: number;
  selectedMerchandiseItemId: string;
};

export const QuickOrderForm: React.FC<QuickOrderFormProps> = ({
  isSubmitting,
  merchandiseError,
  merchandise,
  onMerchandiseItemChange,
  onQuantityChange,
  onSubmit,
  quantityError,
  quantity,
  selectedMerchandiseItemId,
}) => (
  <SectionFormGrid>
    <FormField>
      <FieldLabel>Merchandise item</FieldLabel>
      <FormSelect
        aria-invalid={Boolean(merchandiseError)}
        onChange={(event) => onMerchandiseItemChange(event.target.value)}
        value={selectedMerchandiseItemId}
      >
        <option value="">Select an item</option>
        {merchandise.map((item: any) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
      </FormSelect>
      {merchandiseError ? <FieldErrorText>{merchandiseError}</FieldErrorText> : null}
    </FormField>

    <FormField>
      <FieldLabel>Quantity</FieldLabel>
      <FormInput
        aria-invalid={Boolean(quantityError)}
        min={1}
        onChange={(event) => onQuantityChange(Number(event.target.value))}
        type="number"
        value={quantity}
      />
      {quantityError ? <FieldErrorText>{quantityError}</FieldErrorText> : null}
    </FormField>

    <PrimaryActionButton
      disabled={!selectedMerchandiseItemId || isSubmitting}
      onClick={onSubmit}
      type="button"
    >
      {isSubmitting ? "Placing..." : "Place order"}
    </PrimaryActionButton>
  </SectionFormGrid>
);

type BatchCheckoutFormProps = {
  formError?: string;
  isSubmitting: boolean;
  lineItems: CheckoutLineItem[];
  lineItemErrors?: Array<Partial<Record<"merchandise_item_id" | "quantity", string>>>;
  merchandise: any[];
  onAddLineItem: () => void;
  onLineItemChange: (index: number, patch: Partial<CheckoutLineItem>) => void;
  onRemoveLineItem: (index: number) => void;
  onSubmit: () => void;
  submitDisabled: boolean;
};

export const BatchCheckoutForm: React.FC<BatchCheckoutFormProps> = ({
  formError,
  isSubmitting,
  lineItems,
  lineItemErrors = [],
  merchandise,
  onAddLineItem,
  onLineItemChange,
  onRemoveLineItem,
  onSubmit,
  submitDisabled,
}) => (
  <SectionStack>
    {lineItems.map((lineItem, index) => (
      <MutedPanel key={`line-item-${index}`}>
        <PanelHeaderRow>
          <PanelTitleText>Line item {index + 1}</PanelTitleText>
          <IconActionButton
            disabled={lineItems.length === 1}
            onClick={() => onRemoveLineItem(index)}
            type="button"
          >
            x
          </IconActionButton>
        </PanelHeaderRow>

        <PanelBodyStack>
          <SectionFormGrid>
            <FormField>
              <FieldLabel>Merchandise item</FieldLabel>
              <FormSelect
                aria-invalid={Boolean(lineItemErrors[index]?.merchandise_item_id)}
                onChange={(event) =>
                  onLineItemChange(index, { merchandise_item_id: event.target.value })
                }
                value={lineItem.merchandise_item_id}
              >
                <option value="">Select an item</option>
                {merchandise.map((item: any) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </FormSelect>
              {lineItemErrors[index]?.merchandise_item_id ? (
                <FieldErrorText>{lineItemErrors[index]?.merchandise_item_id}</FieldErrorText>
              ) : null}
            </FormField>

            <FormField>
              <FieldLabel>Quantity</FieldLabel>
              <FormInput
                aria-invalid={Boolean(lineItemErrors[index]?.quantity)}
                min={1}
                onChange={(event) =>
                  onLineItemChange(index, { quantity: Number(event.target.value) || 1 })
                }
                type="number"
                value={lineItem.quantity}
              />
              {lineItemErrors[index]?.quantity ? (
                <FieldErrorText>{lineItemErrors[index]?.quantity}</FieldErrorText>
              ) : null}
            </FormField>

            <CheckoutOtpCheckboxRow>
              <input
                checked={Boolean(lineItem.otp_required)}
                onChange={(event) =>
                  onLineItemChange(index, { otp_required: event.target.checked })
                }
                type="checkbox"
              />
              <CheckoutOtpCheckboxText>Require OTP for this line item</CheckoutOtpCheckboxText>
            </CheckoutOtpCheckboxRow>
          </SectionFormGrid>
        </PanelBodyStack>
      </MutedPanel>
    ))}

    <SecondaryActionButton onClick={onAddLineItem} type="button">
      Add line item
    </SecondaryActionButton>
    {formError ? <FieldErrorText>{formError}</FieldErrorText> : null}
    <PrimaryActionButton disabled={submitDisabled} onClick={onSubmit} type="button">
      {isSubmitting ? "Processing..." : "Run checkout"}
    </PrimaryActionButton>
  </SectionStack>
);
